// HTTP client for the backend Worker: login, session token, gamemaster RPC,
// and the off-limits geocode proxy.

export const API_BASE =
  import.meta.env.VITE_API_BASE || "https://jetlag-sync.matt-brounley.workers.dev";

export const WS_BASE = API_BASE.replace(/^http/, "ws") + "/ws";

export interface Identity {
  sub: string;
  gm: boolean;
  gameId?: string;
  teamId?: string;
  role?: "hider" | "seeker";
  name: string;
  acct?: boolean; // cross-game user account session (sub = userId)
  alsoGm?: boolean; // player-view session whose account is also this game's GM
  alsoPlayer?: boolean; // GM-view session whose account also holds a player slot
  imp?: boolean; // GM is impersonating this player to debug their view
}

export interface Account { id: string; email: string; name?: string; photo?: string; emailVerified?: boolean; profileComplete?: boolean }
const identityFromUser = (u: Account): Identity => ({ sub: u.id, gm: false, acct: true, name: u.name || u.email });

const TOKEN_KEY = "jetlag_token";
const IDENTITY_KEY = "jetlag_identity";

// Each tab pins its own identity in sessionStorage (so you can open the
// gamemaster + a seeker + the hider in three tabs of one browser and have each
// keep its role). localStorage is the cross-tab default for brand-new tabs.
function setSession(token: string, identity: Identity) {
  sessionStorage.setItem(TOKEN_KEY, token);
  sessionStorage.setItem(IDENTITY_KEY, JSON.stringify(identity));
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(IDENTITY_KEY, JSON.stringify(identity));
}
export function getToken(): string | null {
  return sessionStorage.getItem(TOKEN_KEY) ?? localStorage.getItem(TOKEN_KEY);
}
export function getIdentity(): Identity | null {
  const raw = sessionStorage.getItem(IDENTITY_KEY) ?? localStorage.getItem(IDENTITY_KEY);
  return raw ? (JSON.parse(raw) as Identity) : null;
}
export function logout() {
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(IDENTITY_KEY);
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(IDENTITY_KEY);
  localStorage.removeItem("jetlag_acct");
  location.reload();
}

// Log in from a magic link (no password). Stores the session like login().
export async function magicLogin(token: string): Promise<Identity> {
  const res = await fetch(`${API_BASE}/auth/magic?token=${encodeURIComponent(token)}`);
  const data = (await res.json()) as { token?: string; identity?: Identity; error?: string };
  if (!res.ok || !data.token || !data.identity) throw new Error(data.error || "This login link is invalid or expired.");
  setSession(data.token, data.identity);
  return data.identity;
}

export async function login(username: string, password: string): Promise<Identity> {
  const res = await fetch(`${API_BASE}/auth/login`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ username, password }),
  });
  const data = (await res.json()) as { token?: string; identity?: Identity; error?: string };
  if (!res.ok || !data.token || !data.identity) throw new Error(data.error || "Login failed");
  setSession(data.token, data.identity);
  return data.identity;
}

// ---- Account auth (email + password) ------------------------------------
async function accountAuth(path: string, body: Record<string, unknown>): Promise<Account> {
  const res = await fetch(`${API_BASE}${path}`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body) });
  const data = (await res.json()) as { token?: string; user?: Account; error?: string };
  if (!res.ok || !data.token || !data.user) throw new Error(data.error || "Authentication failed");
  const identity = identityFromUser(data.user);
  setSession(data.token, identity);
  localStorage.setItem("jetlag_acct", JSON.stringify({ token: data.token, identity }));
  return data.user;
}
export const accountSignup = (email: string, name: string, password: string) => accountAuth("/auth/signup", { email, name, password });
export const accountLogin = (email: string, password: string) => accountAuth("/auth/account-login", { identifier: email, password });

// ---- Account games + game sessions --------------------------------------
const ACCT_KEY = "jetlag_acct"; // durable "home" account session (to return to from a game)

export interface GameSummary { id: string; name: string; mode: string; isGm: boolean; teamId?: string; teamName?: string; role: string; displayName?: string; memberCount?: number }
export async function updateProfileApi(body: { name?: string; photo?: string | null; profileComplete?: boolean }): Promise<Account> {
  const r = (await authed("/auth/profile", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body) })) as { user?: Account; error?: string };
  if (!r.user) throw new Error(r.error || "Could not save profile");
  return r.user;
}
export async function setMyDisplayNameApi(gameId: string, displayName: string): Promise<void> {
  await authed("/games/display-name", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ gameId, displayName }) });
}
export async function changePasswordApi(current: string, next: string): Promise<void> {
  await authed("/auth/change-password", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ current, next }) });
}
// Pod Rotation roster: add a named placeholder player (no invite), and later get
// a shareable magic login link for them.
export async function addRosterPlayerApi(gameId: string, name: string): Promise<{ userId: string; name: string }> {
  return (await authed("/games/roster-add", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ gameId, name }) })) as { userId: string; name: string };
}
export async function rosterLinkApi(gameId: string, userId: string): Promise<string> {
  return ((await authed("/games/roster-link", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ gameId, userId }) })) as { link: string }).link;
}
export async function myGamesList(): Promise<GameSummary[]> {
  const r = (await authed("/games/mine")) as { games?: GameSummary[] };
  return r.games ?? [];
}
export async function createGameApi(name: string, mode: string): Promise<{ id: string; name: string }> {
  const r = (await authed("/games/create", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ name, mode }) })) as { game?: { id: string; name: string }; error?: string };
  if (!r.game) throw new Error(r.error || "Could not create game");
  return r.game;
}
export async function deleteGameApi(gameId: string): Promise<void> {
  const r = (await authed("/games/delete", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ gameId }) })) as { ok?: boolean; error?: string };
  if (!r.ok) throw new Error(r.error || "Could not delete game");
}
// Swap the active session to a game-scoped token (keeps the account "home").
export async function enterGameSession(gameId: string, viewAs?: "gm" | "player"): Promise<Identity> {
  const r = (await authed("/auth/enter", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ gameId, viewAs }) })) as { token?: string; identity?: Identity; error?: string };
  if (!r.token || !r.identity) throw new Error(r.error || "Could not enter game");
  setSession(r.token, r.identity);
  return r.identity;
}
// GM debug: load a player's exact view (impersonation). Swaps the game session
// to a player-scoped token for `userId`; the account session (jetlag_acct) is
// kept so backToGm() can restore the GM.
export async function viewAsPlayer(gameId: string, userId: string): Promise<Identity> {
  const r = (await authed("/games/view-as", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ gameId, userId }) })) as { token?: string; identity?: Identity; error?: string };
  if (!r.token || !r.identity) throw new Error(r.error || "Could not view that player");
  setSession(r.token, r.identity);
  return r.identity;
}
export async function backToGm(gameId: string): Promise<void> {
  exitToAccount();              // restore the GM's own account session
  await enterGameSession(gameId, "gm");
}

// ---- Game membership management (GM) + invite claim --------------------
export interface GameMember { userId: string; name: string; email?: string; photo?: string; isGm: boolean; teamId?: string }
export interface PendingInvite { id: string; email: string; teamId?: string }
export interface MemberTeam { id: string; name: string; role: "hider" | "seeker" }
export async function getGameMembers(gameId: string): Promise<{ members: GameMember[]; pending: PendingInvite[]; teams: MemberTeam[]; mode: string }> {
  return (await authed(`/games/members?game=${encodeURIComponent(gameId)}`)) as { members: GameMember[]; pending: PendingInvite[]; teams: MemberTeam[]; mode: string };
}
export async function inviteToGameApi(gameId: string, email: string, teamId?: string): Promise<{ added?: boolean; pending?: boolean; error?: string }> {
  return (await authed("/games/invite", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ gameId, email, teamId }) })) as { added?: boolean; pending?: boolean };
}
export async function setMemberTeam(gameId: string, userId: string, teamId: string): Promise<void> {
  await authed("/games/set-team", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ gameId, userId, teamId }) });
}
export async function removeMember(gameId: string, userId: string): Promise<void> {
  await authed("/games/remove-member", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ gameId, userId }) });
}
export async function claimInviteApi(token: string): Promise<{ ok?: boolean; gameId?: string; error?: string }> {
  return (await authed("/games/claim", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ token }) })) as { ok?: boolean; gameId?: string };
}

// Restore the account "home" session (return from a game to the dashboard).
export function exitToAccount(): void {
  const raw = localStorage.getItem(ACCT_KEY);
  if (raw) { try { const a = JSON.parse(raw) as { token: string; identity: Identity }; setSession(a.token, a.identity); return; } catch { /* fall through */ } }
  logout();
}

export async function forgotPassword(email: string): Promise<void> {
  await fetch(`${API_BASE}/auth/forgot`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ email }) });
}
export async function resetPassword(token: string, password: string): Promise<void> {
  const res = await fetch(`${API_BASE}/auth/reset`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ token, password }) });
  const data = (await res.json()) as { ok?: boolean; error?: string };
  if (!res.ok || !data.ok) throw new Error(data.error || "Could not reset password");
}
export async function fetchMe(): Promise<Account | null> {
  try { const r = await authed("/auth/me") as { user?: Account }; return r.user ?? null; } catch { return null; }
}
export async function resendVerify(): Promise<void> {
  await authed("/auth/resend-verify", { method: "POST" });
}

async function authed(path: string, init: RequestInit = {}): Promise<unknown> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: { ...(init.headers || {}), Authorization: `Bearer ${getToken() ?? ""}` },
  });
  if (res.status === 401 || res.status === 403) {
    logout();
    throw new Error("Session expired — please log in again");
  }
  const data = await res.json();
  if (!res.ok) throw new Error((data as { error?: string }).error || "Request failed");
  return data;
}

// ---- Gamemaster admin ----------------------------------------------------
export type GameMode = "seekingteams" | "group";
export interface AdminState {
  games: { id: string; name: string; createdAt: number; mode: GameMode }[];
  teams: { id: string; gameId: string; name: string; role: "hider" | "seeker" }[];
  players: { id: string; gameId: string; teamId: string; username: string; displayName: string; email?: string }[];
}

export interface Roster {
  teams: { id: string; name: string; role: "hider" | "seeker" }[];
  players: { id: string; displayName: string; teamId: string }[];
}

export function adminState(): Promise<AdminState> {
  return authed("/admin/state") as Promise<AdminState>;
}

export function roster(gameId?: string): Promise<Roster> {
  return authed(`/roster${gameId ? `?game=${encodeURIComponent(gameId)}` : ""}`) as Promise<Roster>;
}

export function adminAction(action: string, params: Record<string, unknown> = {}): Promise<unknown> {
  return authed("/admin", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ action, ...params }),
  });
}

export async function geocodeArea(q: string): Promise<{ name: string; geometry: GeoJSON.Geometry }> {
  return authed(`/geocode?q=${encodeURIComponent(q)}`) as Promise<{ name: string; geometry: GeoJSON.Geometry }>;
}

// Nearest street corner (e.g. "E 53rd St & 3rd Ave") for recording where a question was asked from.
export async function nearestCorner(lat: number, lng: number): Promise<string | null> {
  try { const d = (await authed(`/corner?lat=${lat}&lng=${lng}`)) as { corner: string | null }; return d.corner ?? null; }
  catch { return null; }
}

// ---- hider→seeker photos ---------------------------------------------------
// Downscale + re-encode to WebP. Re-encoding through a <canvas> strips ALL EXIF
// (including GPS) — essential so a hider's photo can't leak their exact location.
export async function compressImage(file: Blob, maxPx = 1280, quality = 0.7): Promise<Blob> {
  const bmp = await createImageBitmap(file);
  const scale = Math.min(1, maxPx / Math.max(bmp.width, bmp.height));
  const w = Math.max(1, Math.round(bmp.width * scale)), h = Math.max(1, Math.round(bmp.height * scale));
  const canvas = document.createElement("canvas"); canvas.width = w; canvas.height = h;
  canvas.getContext("2d")!.drawImage(bmp, 0, 0, w, h);
  bmp.close?.();
  const blob = await new Promise<Blob | null>((res) => canvas.toBlob(res, "image/webp", quality));
  return blob ?? file;
}
export async function uploadPhoto(gameId: string, target: string, blob: Blob): Promise<string> {
  const res = await fetch(`${API_BASE}/photo?game=${encodeURIComponent(gameId)}&target=${encodeURIComponent(target)}`, {
    method: "POST", headers: { Authorization: `Bearer ${getToken() ?? ""}`, "content-type": blob.type || "image/webp" }, body: blob,
  });
  if (!res.ok) throw new Error(`upload failed (${res.status})`);
  return ((await res.json()) as { key: string }).key;
}
export function photoUrl(key: string): string { return `${API_BASE}/photo?k=${encodeURIComponent(key)}`; }

export interface InviteResult { id: string; displayName: string; email?: string; link: string; emailed: boolean }

// Send invites (emails magic links to those with an email); returns links for all.
export function sendInvites(gameId: string, ids?: string[]): Promise<{ results: InviteResult[] }> {
  return authed("/invite", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ gameId, ids }) }) as Promise<{ results: InviteResult[] }>;
}

export function inviteLinks(gameId: string): Promise<{ links: InviteResult[] }> {
  return authed(`/invite-links?game=${encodeURIComponent(gameId)}`) as Promise<{ links: InviteResult[] }>;
}
